import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import styles from "../../Styles/previewPDF.module.css";
import genericStyles from "../../Styles/genericStyles.module.css";
import { CustomThemeContext } from "../../Context/CustomThemeProvider";
import { Vocabulary } from "../../Utils/Vocabulary";
import { PreviewDocumentProps } from "../../Utils/Types";
import LargePDFViewer from "./LargePDFViewer";

/**
 * DocumentViewer Component
 *
 * High-performance PDF viewer that handles both small and large PDFs efficiently.
 * Supports lazy-loading of multiple documents - loads the first document initially,
 * then loads subsequent documents as the user scrolls.
 *
 * Features:
 * - Streaming PDF support for files up to 100MB+
 * - Lazy-loading of multiple documents on scroll
 * - Full pagination and zoom controls
 * - Search functionality
 * - Mobile-responsive fullscreen dialog
 * - Automatic browser PDF viewer fallback
 * - Document counter showing progress through documents
 */
function DocumentViewer(props: PreviewDocumentProps & { defaultScale?: number }) {
  const { urls, open, onClose, title, customTopMenu } = props;
  const themeContext: any = useContext(CustomThemeContext);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [loadedDocuments, setLoadedDocuments] = useState<Set<number>>(new Set([0]));
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

  // Handle scroll event to lazy-load documents
  useEffect(() => {
    if (!urls || urls.length === 0) return;

    const handleScroll = () => {
      const content = dialogContentRef.current;
      if (!content) return;

      const scrollPercentage =
        (content.scrollLeft + content.clientWidth) / content.scrollWidth;

      // Determine which document should be active based on scroll position
      const docIndex = Math.floor(scrollPercentage * urls.length);
      const activeIndex = Math.min(docIndex, urls.length - 1);
      setCurrentDocumentIndex(activeIndex);

      // Load documents as user scrolls
      // Load the next document when scrolled more than 70% through current one
      const shouldLoadNext = scrollPercentage > 0.7 && activeIndex + 1 < urls.length;
      if (shouldLoadNext) {
        setLoadedDocuments((prev) => {
          const newSet = new Set(prev);
          newSet.add(activeIndex + 1);
          return newSet;
        });
      }

      // Also preload the document just before if user scrolls backward
      if (activeIndex > 0 && activeIndex - 1 >= 0) {
        setLoadedDocuments((prev) => {
          if (!prev.has(activeIndex - 1)) {
            const newSet = new Set(prev);
            newSet.add(activeIndex - 1);
            return newSet;
          }
          return prev;
        });
      }
    };

    const content = dialogContentRef.current;
    if (content) {
      content.addEventListener("scroll", handleScroll);
      return () => content.removeEventListener("scroll", handleScroll);
    }
  }, [urls]);

  if (!urls || urls.length === 0) {
    return null;
  }

  return (
    <Dialog
      style={{ zIndex: 2000 }}
      maxWidth="lg"
      fullWidth
      fullScreen
      classes={{ paper: styles.overflowHidden }}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="document-viewer-title"
    >
      <DialogTitle id="document-viewer-title" className={genericStyles.fullWidthElement}>
        <div style={{ flexGrow: 2 }}>
          {title}
          {urls.length > 1 && (
            <Typography variant="caption" display="block" style={{ marginTop: "4px" }}>
              Document {currentDocumentIndex + 1} of {urls.length}
            </Typography>
          )}
        </div>
        <div>{customTopMenu}</div>
      </DialogTitle>

      <DialogContent
        ref={dialogContentRef}
        className={styles.dialogContent}
        style={{
          padding: 0,
          flex: 1,
          display: "flex",
          overflow: urls.length > 1 ? "auto" : "hidden",
          scrollBehavior: "smooth",
        }}
      >
        {urls.length === 1 ? (
          // Single document - render directly
          <LargePDFViewer
            pdfUrl={urls[0]}
            title=""
            height="100%"
            width="100%"
            onError={(error) => {
              console.error("PDF loading error:", error);
            }}
          />
        ) : (
          // Multiple documents - render as horizontal scrollable list with lazy loading
          <Box
            style={{
              display: "flex",
              width: "100%",
              minHeight: "100%",
            }}
          >
            {urls.map((url, index) => (
              <Box
                key={`doc-${index}`}
                style={{
                  flex: `0 0 100%`,
                  minWidth: "100%",
                  minHeight: "100%",
                  position: "relative",
                  backgroundColor: "#f5f5f5",
                }}
              >
                {loadedDocuments.has(index) ? (
                  <LargePDFViewer
                    pdfUrl={url}
                    title=""
                    height="100%"
                    width="100%"
                    onError={(error) => {
                      console.error(`PDF loading error for document ${index + 1}:`, error);
                    }}
                  />
                ) : (
                  // Placeholder while document is pending load
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      gap: "16px",
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary">
                      Document {index + 1} - Pending load
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Scroll to load this document
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions classes={{ root: styles.actionsWrapper }}>
        {urls.length > 1 && (
          <Typography
            variant="caption"
            style={{ flex: 1, paddingLeft: "16px", color: "#666" }}
          >
            {loadedDocuments.size} of {urls.length} documents loaded
          </Typography>
        )}
        <Button
          size="small"
          onClick={() => onClose(false)}
          style={{ background: themeContext.theme?.palette.cancel?.main }}
        >
          {Vocabulary.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DocumentViewer;
